import React, { useRef, useMemo, useState } from "react";

import useBarChart from "./hooks/useBarChart/useBarChart";
import ChartData, { Dataset } from "../types/ChartData";
import calculateBarChartMinWidth from "./helpers/calculateBarChartMinWidth";

import Card, { CardProps } from "../../common/Card/Card";
import Typography from "../../common/Typography/Typography";
import Legend from "../Legend/Legend";
import CardActions from "../../common/Card/CardActions";

import styles from "./BarChart.module.css";

interface Props {
    padding?: CardProps["padding"];
    title: string;
    data: ChartData;
    showLegend?: boolean;
}

const BarChart = ({ padding, title, data, showLegend = true }: Props) => {
    // Array of data to be hidden in the bar chart.
    const [hiddenData, setHiddenData] = useState<Dataset["label"][]>([]);

    const legend = useMemo(
        () =>
            data.datasets.map(({ label, backgroundColor }) => ({
                label,
                backgroundColor,
                hidden: hiddenData.includes(label),
            })),
        [data, hiddenData]
    );

    // Add and remove selected data from the hidden data array.
    const toggleLegend = (label: Dataset["label"]) => {
        if (hiddenData.includes(label)) {
            return setHiddenData((state) => state.filter((hiddenLabel) => hiddenLabel !== label));
        }

        return setHiddenData((state) => [...state, label]);
    };

    // Filter out hidden data from the chart data.
    const barChartData = useMemo(
        () => ({
            ...data,
            datasets: data.datasets.filter(({ label }) => !hiddenData.includes(label)),
        }),
        [data, hiddenData]
    );

    const canvasRef = useRef(null);
    useBarChart(canvasRef, barChartData);
    const minWidth = calculateBarChartMinWidth(
        data.datasets.filter((dataset) => !hiddenData.includes(dataset.label)).length,
        data.labels.length
    );

    return (
        <Card padding={padding} className="space-y-2  whitespace-nowrap">
            {title ? (
                <Typography as="h3" variant="heading">
                    {title}
                </Typography>
            ) : null}
            <div className="overflow-x-auto overflow-y-hidden pb-0.5">
                <div className={styles.barChart} style={{ minWidth }}>
                    <canvas ref={canvasRef} />
                </div>
            </div>
            <CardActions>{showLegend ? <Legend legend={legend} onClick={toggleLegend} /> : null}</CardActions>
        </Card>
    );
};

export default BarChart;
